import rest from '../Utils/rest'

// This file contains API functions for Super Admin operations on public templates
// All functions are prefixed with SA_ to indicate they are for Super Admin use only

/**
 * Create a new public template
 * 
 * @param {Object} params - Parameters for the API call
 * @param {string} params.templateName - Name of the template
 * @param {string} params.templateSubject - Subject of the template
 * @param {string} [params.description] - Description of the template
 * @param {Array} [params.sections] - Array of course sections with their content
 * @param {boolean} [params.isPubliclyAccessible] - Whether the template is publicly accessible
 * @param {Array<string>} [params.forCompanyIds] - Array of company IDs that can access this template
 * @param {boolean} [params.isReady] - Whether the template is ready for use
 * @param {Array<string>} [params.tags] - Array of tags for the template
 * @param {number} [params.estimatedDuration] - Estimated duration in minutes
 * @param {Object} [params.metadata] - Additional metadata for the template
 * @returns {Promise<Object>} - Promise that resolves to the created template
 */
export const SA_createPublicTemplateAPI = (params) => {
    return rest.jsonPostUrl(`/api/sa/publicTemplates/create`, params);
}

/**
 * Get all public templates with pagination
 * 
 * @param {Object} params - Parameters for the API call
 * @param {Object} [params.paginationModel] - Pagination model
 * @param {number} [params.paginationModel.page=0] - Page number
 * @param {number} [params.paginationModel.pageSize=10] - Items per page
 * @param {Object} [params.sortModel] - Sort model
 * @param {string} [params.sortModel.field] - Field to sort by
 * @param {string} [params.sortModel.sort] - Sort direction ('asc' or 'desc')
 * @param {Object} [params.filterModel] - Filter model
 * @param {Object} [params.filterModel.filterValues] - Filter values
 * @returns {Promise<Array>} - Promise that resolves to an array of templates
 */
export const SA_getAllPublicTemplatesAPI = ({ 
    paginationModel = { page: 0, pageSize: 10 }, 
    sortModel = { field: 'createdAt', sort: 'desc' },
    filterModel
}) => {
    return rest.jsonPostUrl(`/api/sa/publicTemplates/all`, {
        paginationModel,
        sortModel,
        filterModel
    });
}

/**
 * Get a public template by ID
 * 
 * @param {Object} params - Parameters for the API call
 * @param {string} params.id - Template ID
 * @returns {Promise<Object>} - Promise that resolves to the template
 */
export const SA_getPublicTemplateByIdAPI = ({ id }) => {
    console.log('SA_getPublicTemplateByIdAPI called with id:', id);

    // Check if id is defined
    if (!id) {
        console.error('Template ID is undefined in SA_getPublicTemplateByIdAPI');
        throw new Error('Template ID is required');
    }

    return rest.getUrl(`/api/sa/publicTemplates/${id}`);
}

/**
 * Update a public template
 * 
 * @param {Object} params - Parameters for the API call
 * @param {string} params.id - Template ID
 * @param {string} [params.templateName] - Name of the template
 * @param {string} [params.templateSubject] - Subject of the template
 * @param {string} [params.description] - Description of the template
 * @param {Array} [params.sections] - Array of course sections with their content
 * @param {boolean} [params.isPubliclyAccessible] - Whether the template is publicly accessible
 * @param {Array<string>} [params.forCompanyIds] - Array of company IDs that can access this template
 * @param {boolean} [params.isReady] - Whether the template is ready for use
 * @param {Array<string>} [params.tags] - Array of tags for the template
 * @param {number} [params.estimatedDuration] - Estimated duration in minutes
 * @param {Object} [params.metadata] - Additional metadata for the template
 * @returns {Promise<Object>} - Promise that resolves to the updated template
 */
export const SA_updatePublicTemplateAPI = ({ id, ...updateData }) => {
    return rest.jsonPutUrl(`/api/sa/publicTemplates/${id}`, updateData);
}

/**
 * Delete a public template
 *
 * @param {Object} params - Parameters for the API call
 * @param {string} params.id - Template ID
 * @returns {Promise<Object>} - Promise that resolves to a success message
 */
export const SA_deletePublicTemplateAPI = ({ id }) => {
    return rest.jsonPostUrl(`/api/sa/publicTemplates/${id}/delete`, {});
}

/**
 * Duplicate a public template
 * The backend automatically generates the name as "copy_${originalName}"
 *
 * @param {Object} params - Parameters for the API call
 * @param {string} params.id - Template ID to duplicate
 * @returns {Promise<Object>} - Promise that resolves to the duplicated template
 */
export const SA_duplicatePublicTemplateAPI = ({ id }) => {
    return rest.jsonPostUrl(`/api/sa/publicTemplates/${id}/duplicate`, {});
}

/**
 * Get public templates by company ID
 * 
 * @param {Object} params - Parameters for the API call
 * @param {string} params.companyId - Company ID
 * @returns {Promise<Array>} - Promise that resolves to an array of templates
 */
export const SA_getPublicTemplatesByCompanyIdAPI = ({ companyId }) => {
    return rest.getUrl(`/api/sa/publicTemplates/company/${companyId}`);
}

/**
 * Get public templates by creator ID
 * 
 * @param {Object} params - Parameters for the API call
 * @param {string} params.userId - User ID
 * @returns {Promise<Array>} - Promise that resolves to an array of templates
 */
export const SA_getPublicTemplatesByCreatorIdAPI = ({ userId }) => {
    return rest.getUrl(`/api/sa/publicTemplates/creator/${userId}`);
}

/**
 * Get public templates by subject
 * 
 * @param {Object} params - Parameters for the API call
 * @param {string} params.subject - Template subject
 * @returns {Promise<Array>} - Promise that resolves to an array of templates
 */
export const SA_getPublicTemplatesBySubjectAPI = ({ subject }) => {
    return rest.getUrl(`/api/sa/publicTemplates/subject/${subject}`);
}

/**
 * Get public templates by tags
 * 
 * @param {Object} params - Parameters for the API call
 * @param {Array<string>} params.tags - Array of tags
 * @returns {Promise<Array>} - Promise that resolves to an array of templates
 */
export const SA_getPublicTemplatesByTagsAPI = ({ tags }) => {
    const tagsString = Array.isArray(tags) ? tags.join(',') : tags;
    return rest.getUrl(`/api/sa/publicTemplates/tags?tags=${tagsString}`);
}

/**
 * Search public templates
 * 
 * @param {Object} params - Parameters for the API call
 * @param {string} params.searchTerm - Search term
 * @returns {Promise<Array>} - Promise that resolves to an array of templates
 */
export const SA_searchPublicTemplatesAPI = ({ searchTerm }) => {
    return rest.getUrl(`/api/sa/publicTemplates/search?searchTerm=${searchTerm}`);
}
